import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface AdminNotification {
  id: string;
  event_type: string;
  event_category: string;
  title: string;
  details: Record<string, unknown> | null;
  related_id: string | null;
  related_table: string | null;
  priority: string;
  read_at: string | null;
  email_sent: boolean | null;
  created_at: string;
}

interface UseAdminNotificationsOptions {
  enableRealtime?: boolean;
  enableSound?: boolean;
  limit?: number;
}

export function useAdminNotifications(options: UseAdminNotificationsOptions = {}) {
  const { enableRealtime = true, enableSound = true, limit = 50 } = options;
  const [notifications, setNotifications] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  // Fetch initial notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from("admin_notifications")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;

      const typedData = (data || []) as AdminNotification[];
      setNotifications(typedData);
      setUnreadCount(typedData.filter((n) => !n.read_at).length);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (!enableSound) return;
    try {
      const audio = new Audio("/notification.mp3");
      audio.volume = 0.3;
      audio.play().catch(() => {});
    } catch (e) {
      // Ignore audio errors
    }
  }, [enableSound]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("admin_notifications")
        .update({ read_at: new Date().toISOString() })
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  }, []);

  // Mark all as read
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadIds = notifications.filter((n) => !n.read_at).map((n) => n.id);
      if (unreadIds.length === 0) return;

      const { error } = await supabase
        .from("admin_notifications")
        .update({ read_at: new Date().toISOString() })
        .in("id", unreadIds);

      if (error) throw error;

      setNotifications((prev) =>
        prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  }, [notifications]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from("admin_notifications")
        .delete()
        .eq("id", notificationId);

      if (error) throw error;

      setNotifications((prev) => {
        const deleted = prev.find((n) => n.id === notificationId);
        if (deleted && !deleted.read_at) {
          setUnreadCount((c) => Math.max(0, c - 1));
        }
        return prev.filter((n) => n.id !== notificationId);
      });
    } catch (error) {
      console.error("Error deleting notification:", error);
      toast({
        title: "Error",
        description: "No se pudo eliminar la notificación",
        variant: "destructive",
      });
    }
  }, [toast]);

  // Delete all notifications
  const deleteAllNotifications = useCallback(async () => {
    try {
      const ids = notifications.map((n) => n.id);
      if (ids.length === 0) return;

      const { error } = await supabase
        .from("admin_notifications")
        .delete()
        .in("id", ids);

      if (error) throw error;

      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      toast({
        title: "Error",
        description: "No se pudieron eliminar las notificaciones",
        variant: "destructive",
      });
    }
  }, [notifications, toast]);

  // Set up realtime subscription
  useEffect(() => {
    fetchNotifications();

    if (!enableRealtime) return;

    const channel = supabase
      .channel("admin-notifications-realtime")
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "admin_notifications",
        },
        (payload) => {
          const newNotification = payload.new as AdminNotification;
          
          setNotifications((prev) => [newNotification, ...prev.slice(0, limit - 1)]);
          setUnreadCount((prev) => prev + 1);
          
          playNotificationSound();
          
          // Show toast for important/critical notifications
          if (newNotification.priority === "important" || newNotification.priority === "critical") {
            toast({
              title: newNotification.title,
              description: `${newNotification.event_category} • ${newNotification.event_type}`,
              variant: newNotification.priority === "critical" ? "destructive" : "default",
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enableRealtime, fetchNotifications, limit, playNotificationSound, toast]);

  return {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
    refetch: fetchNotifications,
  };
}
